import { FC } from 'react';
import { CgMail, CgPhone } from 'react-icons/cg';
import { Box, Flex, FlexProps, Link, Text } from '@chakra-ui/react';

import { Entity } from '~/core/shared/types';

type PrimaryContactProps = {
  application: Entity;
};

type ContactBlockProps = FlexProps & {
  isBroker: boolean;
  firstName?: string | undefined;
  lastName?: string | undefined;
  name?: string;
  roleWithinCompany?: string;
  positionTitle?: string;
  isAccredited?: boolean;
  phone?: string | undefined;
  email?: string | undefined;
  company?: string;
  relationshipToApplicantEntity?: string;
};

const ContactBlock: FC<ContactBlockProps> = ({
  isBroker,
  firstName,
  lastName,
  name,
  roleWithinCompany,
  isAccredited = false,
  phone,
  email,
  positionTitle,
  company,
  relationshipToApplicantEntity,
  ...props
}) => {
  const contactName = name || `${firstName} ${lastName}`;

  return (
    <Flex direction="column" {...props}>
      <Text>{contactName}</Text>

      {roleWithinCompany && (
        <Text color="gray.600" mr={2}>
          {roleWithinCompany}
        </Text>
      )}

      {company && (
        <Text color="primary.700" fontSize="14px">
          {company || ''}
        </Text>
      )}

      {!isBroker && relationshipToApplicantEntity && (
        <Text color="gray.600" fontSize="sm" mr={2}>
          {relationshipToApplicantEntity}
        </Text>
      )}

      <Box fontSize="14px">
        <Flex direction="row">
          <Text color="gray.600" mr={2}>
            {isBroker ? 'Broker' : positionTitle}
          </Text>

          {isBroker && !isAccredited && (
            <Text
              bgColor="orange.100"
              fontSize="12px"
              px={2}
              py="px"
              rounded="lg"
              textTransform="uppercase"
            >
              unaccredited
            </Text>
          )}

          {isBroker && isAccredited && (
            <Text
              bgColor="blue.100"
              fontSize="12px"
              px={2}
              py="px"
              rounded="lg"
              textTransform="uppercase"
            >
              accredited
            </Text>
          )}
        </Flex>

        {phone && (
          <Flex alignItems="center" mt={4} my={2}>
            <CgPhone />
            <Link href={`tel:${phone}`} ml={1}>
              {phone as string}
            </Link>
          </Flex>
        )}

        {email && (
          <Flex alignItems="center" my={2}>
            <CgMail />
            <Text ml={1}>{email as string}</Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export const PrimaryContact: FC<PrimaryContactProps> = ({ application }) => {
  const isBroker = application.applicantType === 'broker';
  const isAccredited = application.accreditedBroker === true;
  const applicantName = `${application.firstName} ${application.lastName}`;
  const hasCustomerInfo = !!(application.firstName || application.lastName);
  let brokerName = '';
  if (isBroker) {
    brokerName = application.brokerName
      ? (application.brokerName as string)
      : `${application.brokerFirstName} ${application.brokerLastName}`;
  }
  const hasBrokerAndApplicantInfo = !!(
    isBroker && applicantName !== brokerName
  );
  const hasBDMContactInfo = !!(
    application.bdmContactName && application.bdmContactEmail
  );

  return (
    <Flex direction="column">
      {hasCustomerInfo && (
        <ContactBlock
          email={application.email as string}
          firstName={application.firstName as string}
          isBroker={hasBrokerAndApplicantInfo ? false : isBroker}
          lastName={application.lastName as string}
          name={applicantName}
          phone={
            (application.phone as string) ||
            (application.mobileNumber as string)
          }
          relationshipToApplicantEntity={
            application.relationshipToApplicantEntity as string | undefined
          }
          roleWithinCompany={application.roleWithinCompany as string}
        />
      )}

      {hasBrokerAndApplicantInfo && (
        <ContactBlock
          borderTop={hasCustomerInfo ? 'solid 1px' : ''}
          borderTopColor="gray.200"
          company={application.brokerageName as string}
          email={application.brokerEmail as string}
          isAccredited={isAccredited}
          isBroker
          mt={hasCustomerInfo ? 2 : 0}
          name={brokerName || ''}
          phone={application.brokerPhone as string}
          pt={hasCustomerInfo ? 2 : 0}
        />
      )}
      {hasBDMContactInfo && (
        <ContactBlock
          borderTop="solid 1px"
          borderTopColor="gray.200"
          email={application.bdmContactEmail as string}
          isAccredited={isAccredited}
          isBroker={false}
          mt={2}
          name={application.bdmContactName as string}
          phone={application.bdmContactPhone as string}
          positionTitle="BDM"
          pt={2}
        />
      )}
    </Flex>
  );
};
